// Refund by dish, on the till: which dishes can still be refunded, and what ticking them
// comes to. The server works the amount out again and is the one believed
// (`orders._refund_for_lines`); this is the same sum, so the button can say it first.
//
// A dish refunds at what it was charged (its line's subtotal, per unit), scaled for a bill
// discount, because that came off every dish.

const pence = (v) => Math.round((Number(v) || 0) * 100);

export function refundableLines(order) {
  const done = order?.refunded_lines || {};
  return (order?.items || [])
    .filter((l) => l?.line_id && !l.voided && Number(l.quantity) > 0)
    .map((l) => ({
      line_id: l.line_id,
      name: l.name,
      ordered: Number(l.quantity),
      left: Number(l.quantity) - Number(done[l.line_id] || 0),
      unitPence: pence(l.subtotal) / Number(l.quantity),
    }));
}

function scaleOf(order) {
  const gross = (order?.items || [])
    .filter((l) => l?.line_id && !l.voided && Number(l.quantity) > 0)
    .reduce((s, l) => s + pence(l.subtotal), 0);
  return gross > 0 ? pence(order?.total) / gross : 0;
}

/** What `picks` ({ line_id: quantity }) refunds, in pounds.
 *
 * Each dish is rounded to the penny on its own, so on a discounted bill the dishes do not
 * always add up to the bill (three at £3.33 with 10% off: £3.00 each, £8.99 paid). So, as
 * the server does: never more than is left of the bill, and the refund that takes the last
 * dish gives back exactly what is left. */
export function amountFor(order, picks) {
  const scale = scaleOf(order);
  const lines = refundableLines(order);
  const byId = Object.fromEntries(lines.map((l) => [l.line_id, l]));
  const p = Object.entries(picks || {}).reduce((sum, [id, qty]) => {
    const line = byId[id];
    return line && qty > 0 ? sum + Math.round(line.unitPence * qty * scale) : sum;
  }, 0);
  const left = Math.max(0, pence(order?.total) - pence(order?.refund_amount));
  const lastDish = lines.length > 0 && lines.every((l) => (Number(picks?.[l.line_id]) || 0) >= l.left);
  // A penny a dish is the most rounding can be out by; a wider gap is not rounding.
  const settle = lastDish && left - p > 0 && left - p <= lines.length ? left : Math.min(p, left);
  return settle / 100;
}

/** Every dish still refundable, all of it: the whole bill in one tap. */
export function allPicks(order) {
  return Object.fromEntries(refundableLines(order).filter((l) => l.left > 0).map((l) => [l.line_id, l.left]));
}
